// #region Imports
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { Theme } from "@mui/material";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import {
  ConfirmResponse,
  useConfirmDialog
} from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import CondensedCell from "_shared/_views/tables/condensed-cell";
import RowActions from "_shared/_views/tables/row-actions";
import RowLink from "_shared/_views/tables/row-link";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import Guid from "_shared/guids/guid";
import TeamAvatar from "marketplace/_views/avatars/team-avatar";
import {
  useDeleteTeamBookmarkMutation,
  useGetTeamBookmarksQuery,
} from "marketplace/bookmarks/_api/bookmark-api";
import { PageTab } from "marketplace/bookmarks/_views/bookmarks-page-tabs";
import TeamBookmarkQuery, {
  TEAM_BOOKMARK_QUERY_MAXIMUM_LENGTH,
  type TeamBookmarkFilters,
  TeamBookmarkOrderField,
} from "marketplace/bookmarks/team-bookmarks/_api/queries/team-bookmark-query";
import BookmarkedTeamListItem from "marketplace/bookmarks/team-bookmarks/bookmarked-team-list-item";
import ViewTeamProfile from "marketplace/team-profile/_views/view-team-profile";
import {
  createMRTColumnHelper,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_TableInstance,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_SortingState as SortingState,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_Updater,
} from "material-react-table";
import {
  MobileToolbarButtonsContainer,
  ScrollableTabs,
  searchFieldSpacing,
  searchTextFieldProps,
  topToolbarFitsThePage,
  ToolbarButtons,
} from "_shared/_views/tables/table-toolbar";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { DateCell } from "_shared/_views/tables/date-cells";
import { formatDate } from "_shared/formatting/dates";
// #endregion

// #region Styled Components
const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const TableTopToolbarContainer = styled("div")(() => ({
  maxWidth: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  minWidth: 0,
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const NameCellContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  minWidth: 0,
}));
// #endregion

// #region Component Input Props
type TeamBookmarksProps = {
  activeTab: PageTab;
};

const columnHelper = createMRTColumnHelper<BookmarkedTeamListItem>();

/** The sortable columns, by the ids the sort state carries. */
export const NAME_COLUMN = "name";
export const DATE_BOOKMARKED_COLUMN = "created";

/** The leader's column. The search matches the leader's name, so the row shows it; it neither sorts nor filters. */
export const LEADER_COLUMN = "leader";

/** The name a row shows when the server does not let the caller read the bookmarked team. */
export const NOT_VISIBLE_NAME = "No access to this team";

/** What a row says for a team the caller may read that has no name, such as a hidden team with no profile. */
export const UNNAMED_TEAM_NAME = "Unnamed team";

/** What the table says when its column filters cannot be sent to the server. */
export const INVALID_FILTERS_MESSAGE = "The date range must end on or after the day it starts.";

/** Newest bookmark first: the server's own default, so the header arrow says what the rows are in. */
export const DEFAULT_SORTING: SortingState = [{ id: DATE_BOOKMARKED_COLUMN, desc: true }];

/**
 * The order the server is asked for, from the order the table shows.
 *
 * Always one column and always a direction. The server orders every page, so an "unsorted" state
 * would be a header arrow that means nothing. `enableSortingRemoval` is off for the same reason.
 */
export function teamBookmarkOrderFrom(sorting: SortingState): {
  orderBy: TeamBookmarkOrderField;
  orderDir: OrderDirection;
} {
  const first = sorting.length > 0 ? sorting[0] : DEFAULT_SORTING[0];
  return {
    orderBy: first.id === NAME_COLUMN
      ? TeamBookmarkOrderField.Name
      : TeamBookmarkOrderField.DateBookmarked,
    orderDir: first.desc ? OrderDirection.Descending : OrderDirection.Ascending,
  };
}

/** Whether the server accepts the search. It refuses a search longer than it can match. */
export function isTeamBookmarksGlobalFilterValid(value: string): boolean {
  return value.trim().length <= TEAM_BOOKMARK_QUERY_MAXIMUM_LENGTH;
}

/** The first year with four digits: an earlier year in a date filter is one still being typed. */
const FIRST_COMPLETE_YEAR = 1000;

/**
 * The local day a date filter names, or undefined when it names none. MRT's date pickers hand over
 * whatever the app's date adapter makes (a moment), so anything with `toDate` is read through it.
 *
 * The picker's field hands over a date at each key typed in the year, so "2026" arrives first as
 * the years 2, 20 and 202. A year below 1000 is a year not yet typed in full, and names no day:
 * otherwise the table sends a request for each key.
 */
function dayOf(value: unknown): Date | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const date = typeof (value as { toDate?: unknown }).toDate === "function"
    ? (value as { toDate: () => Date }).toDate()
    : new Date(value as string | number | Date);
  if (Number.isNaN(date.getTime()) || date.getFullYear() < FIRST_COMPLETE_YEAR) return undefined;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * The server's filters, from the table's column filters, or undefined when they cannot be sent.
 *
 * The name filter is sent as typed. The date range is two days the reader picks, inclusive, where
 * the reader is: it is sent as the start of the first day and the start of the day after the last,
 * so a bookmark made late on the last day is still in it. A range that ends before it starts, or a
 * name longer than the server accepts, cannot be sent.
 */
export function teamBookmarkFiltersFrom(columnFilters: ColumnFiltersState): TeamBookmarkFilters | undefined {
  const name = columnFilters.find((filter) => filter.id === NAME_COLUMN)?.value;
  const range = columnFilters.find((filter) => filter.id === DATE_BOOKMARKED_COLUMN)?.value;
  const [firstDay, lastDay] = Array.isArray(range) ? range : [];
  const bookmarkedFrom = dayOf(firstDay);
  const last = dayOf(lastDay);
  const bookmarkedBefore = last
    ? new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1)
    : undefined;
  const trimmedName = typeof name === "string" ? name.trim() : "";

  if (trimmedName.length > TEAM_BOOKMARK_QUERY_MAXIMUM_LENGTH) return undefined;
  if (bookmarkedFrom && bookmarkedBefore && bookmarkedFrom >= bookmarkedBefore) return undefined;
  return {
    name: trimmedName || undefined,
    bookmarkedFrom,
    bookmarkedBefore,
  };
}

/** True when the row can open the bookmarked team's profile. A row the caller may not read cannot. */
function canViewProfile(row: BookmarkedTeamListItem): boolean {
  return !row.isUnavailable && row.team !== undefined;
}

/** The team's name, or what the row says instead when the caller may not read it or it has none. */
function teamName(row: BookmarkedTeamListItem): string {
  if (row.team === undefined) {
    return NOT_VISIBLE_NAME;
  }
  return row.team.profile?.name || UNNAMED_TEAM_NAME;
}

/** The leader's name, or nothing when the caller may not read the team. */
function leaderName(row: BookmarkedTeamListItem): string {
  return row.team?.leader?.fullName ?? "";
}

/** MRT hands state changes as either a value or a function of the old one. */
function applyUpdater<TState>(updater: MRT_Updater<TState>, current: TState): TState {
  return typeof updater === "function" ? (updater as (old: TState) => TState)(current) : updater;
}

export default function TeamBookmarks(props: Readonly<TeamBookmarksProps>) {
  const { activeTab } = props;

  // #region State
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  // #region Hooks
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const { openDialog } = useDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  // #region Queries
  const isGlobalFilterValid = isTeamBookmarksGlobalFilterValid(globalFilter);
  const filters = useMemo(() => teamBookmarkFiltersFrom(columnFilters), [columnFilters]);

  // No debounce here. MRT's own `GlobalFilterTextField` debounces the typed text and pushes it into
  // `globalFilter` after 500ms while `manualFiltering` is on. A second debounce would make the
  // reader wait twice.
  const query = useMemo(() => {
    if (!isGlobalFilterValid || !filters) return undefined;

    const order = teamBookmarkOrderFrom(sorting);
    return new TeamBookmarkQuery({
      paginationParameters: PaginationParameters.create(pagination),
      orderBy: order.orderBy,
      orderDir: order.orderDir,
      query: globalFilter,
      filters,
    });
  }, [filters, globalFilter, isGlobalFilterValid, pagination, sorting]);

  const bookmarksQuery = useGetTeamBookmarksQuery(query ? { query } : skipToken);
  const [deleteTeamBookmark, { isLoading: isDeleting }] = useDeleteTeamBookmarkMutation();

  const bookmarks = useMemo(() => {
    if (!bookmarksQuery.data) return PaginatedResponse.empty<BookmarkedTeamListItem>();
    return PaginatedResponse.from<BookmarkedTeamListItem>(
      BookmarkedTeamListItem.fromJSONArray(bookmarksQuery.data.data),
      bookmarksQuery.data.headers,
    );
  }, [bookmarksQuery.data]);
  const bookmarksError = bookmarksQuery.isError;

  // #region Lifecycle Hooks
  useEffect(() => {
    if (bookmarksError) {
      enqueueSnackbar("Error loading bookmarks. Please try again.", { variant: 'error' });
    }
  }, [bookmarksError]);

  // #region Event Handlers
  async function handleDeleteBookmark(row: BookmarkedTeamListItem) {
    const response = await confirm({
      title: "Delete Bookmark?",
      message: "Do you want to delete?",
      okButtonText: "Delete",
      cancelButtonText: "Cancel"
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await deleteTeamBookmark({
        bookmarkId: row.bookmark.id.value,
        teamId: row.bookmark.teamId.value,
      }).unwrap();
      enqueueSnackbar("Deleted bookmark", { variant: "success" });
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Couldn't delete bookmark. Please try again", { variant: "error" });
    }
  }

  // Memoized because the name cell's link depends on it, and a fresh function each render would
  // rebuild that cell — and through it the column list — on every render.
  const handleViewProfile = useCallback((profileId: Guid): void => {
    openDialog({
      component: <ViewTeamProfile teamId={profileId} />,
      titleStyle: {
        paddingBottom: 0
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden"
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: false
      }
    });
  }, [openDialog]);

  /**
   * What opening the row does: the row's click, the name's link and the ⋯'s Open all come from here.
   * Nothing for a row the caller may not read.
   */
  const profileOpenerOf = useCallback((row: BookmarkedTeamListItem): (() => void) | undefined => {
    if (!canViewProfile(row)) return undefined;
    return () => handleViewProfile(row.teamId);
  }, [handleViewProfile]);
  // #endregion

  // #region Columns
  const renderNameColumn = useCallback((
    row: BookmarkedTeamListItem,
    table: MRT_TableInstance<BookmarkedTeamListItem>,
  ) => {
    const onClick = profileOpenerOf(row);

    return (
      <NameCellContainer>
        <TeamAvatar
          team={row.team}
        />
        <CondensedCell
          title={
            // A row the caller may not see opens nothing, so it must not look like a link.
            // The server searches a readable team's own name, never a stand-in (#6590).
            <RowLink onClick={onClick}>
              {row.team?.profile?.name ? <SearchMatch table={table} text={teamName(row)} /> : teamName(row)}
            </RowLink>
          }
        />
      </NameCellContainer>
    );
  }, [profileOpenerOf]);

  const columns = useMemo(() => [
    // Accessor columns, so that MRT shows a sort arrow. The server sorts and filters: the name by
    // the team name, and the date by when the bookmark was made.
    columnHelper.accessor((row) => teamName(row), {
      header: "Name",
      id: NAME_COLUMN,
      enableSorting: true,
      enableColumnFilter: true,
      muiFilterTextFieldProps: { slotProps: { htmlInput: { maxLength: TEAM_BOOKMARK_QUERY_MAXIMUM_LENGTH } } },
      meta: { kind: "name" },
      Cell: ({ row, table }) => renderNameColumn(row.original, table),
    }),
    // The search also matches the leader's name, and MRT marks this plain cell itself.
    columnHelper.accessor((row) => leaderName(row), {
      header: "Leader",
      id: LEADER_COLUMN,
      enableSorting: false,
      enableColumnFilter: false,
      meta: { kind: "person" },
    }),
    columnHelper.accessor((row) => formatDate(row.bookmark?.createdDate) ?? "", {
      header: "Bookmarked",
      id: DATE_BOOKMARKED_COLUMN,
      Cell: ({ row }) => <DateCell value={row.original.bookmark?.createdDate} />,
      enableSorting: true,
      enableColumnFilter: true,
      // The search box never matches the date, so the date never shows a match (#6590).
      enableGlobalFilter: false,
      filterVariant: "date-range",
      meta: { kind: "date" },
    }),
  ], [renderNameColumn]);
  // #endregion

  // #region Table
  const tableOptions: MRT_TableOptions<BookmarkedTeamListItem> = {
    columns,
    data: bookmarks.data,
    enableRowSelection: false,
    enableTableHead: true,
    enableColumnPinning: true,
    enableRowActions: true,
    muiTableBodyRowProps: ({ row }) => {
      const openProfile = profileOpenerOf(row.original);
      if (!openProfile) return {};
      return { onClick: openProfile, sx: { cursor: "pointer" } };
    },
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
              <TableToolsMenu table={table} />
            </ToolbarButtons>
          </ToolbarButtonsContainer>
        )}
      </>
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">
            Bookmarks
          </TableTitle>
          <LowerTopToolbarContainer>
            <ScrollableTabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              onChange={(_event, newValue) => navigate(`/marketplace/bookmarks/${newValue}`)}>
              <Tab value={PageTab.Individual} label="Individual" />
              <Tab value={PageTab.Team} label="Team" />
              <Tab value={PageTab.Company} label="Company" />
            </ScrollableTabs>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <MobileToolbarButtonsContainer>
              <GlobalFilterTextField table={table} />
              <ToolbarButtons>
                <TableToolsMenu table={table} />
              </ToolbarButtons>
            </MobileToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    // While a page loads, MRT fills the table with empty placeholder rows ({}), which have no bookmark.
    getRowId: (row, index) => row.bookmark?.id.value ?? `placeholder-${index}`,
    initialState: {
      showGlobalFilter: true,
      columnPinning: { right: ["mrt-row-actions"] }
    },
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) => ({
      ...searchTextFieldProps({
        searches: "team bookmarks",
        table,
        maxLength: TEAM_BOOKMARK_QUERY_MAXIMUM_LENGTH,
      }),
      error: !isGlobalFilterValid,
      helperText: isGlobalFilterValid
        ? undefined
        : `Search must be ${TEAM_BOOKMARK_QUERY_MAXIMUM_LENGTH} characters or fewer.`,
    }),
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: bookmarksError,
      isLoading: bookmarksQuery.isFetching,
      onRetry: () => void bookmarksQuery.refetch(),
      subject: "team bookmarks",
    }),
    renderRowActions: ({ row }) => {
      const name = row.original.team?.profile?.name ?? "team";
      const openProfile = profileOpenerOf(row.original);
      return (
        <RowActions
          ariaLabel={`Actions for ${name}`}
          open={openProfile && { onClick: openProfile }}
          actions={[
            {
              key: "delete",
              label: "Delete Bookmark",
              ariaLabel: `Delete bookmark for ${name}`,
              icon: <DeleteIcon />,
              destructive: true,
              onClick: () => {
                void handleDeleteBookmark(row.original);
              },
            },
          ]}
        />
      );
    },
    // The server pages, searches, filters and sorts.
    enableColumnFilters: true,
    // A range that ends before it starts is not sent, so the table says why nothing changed.
    renderBottomToolbarCustomActions: () => filters
      ? null
      : <Typography color="error" variant="body2" role="alert">{INVALID_FILTERS_MESSAGE}</Typography>,
    enableMultiSort: false,
    // The server orders every page, so there is no unsorted state for a third click to mean.
    enableSortingRemoval: false,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: {
      sx: {
        flex: 1
      },
    },
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column"
      },
    },
    muiToolbarAlertBannerProps: bookmarksError
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    rowCount: bookmarks.totalElements,
    // A new search, filter or order is a new result set, so the reader goes back to its first page.
    onColumnFiltersChange: (updater: MRT_Updater<ColumnFiltersState>) => {
      setColumnFilters(applyUpdater(updater, columnFilters));
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    onGlobalFilterChange: (updater: MRT_Updater<string>) => {
      setGlobalFilter(applyUpdater(updater, globalFilter) ?? "");
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    onPaginationChange: (updater: MRT_Updater<PaginationState>) => {
      const next = applyUpdater(updater, pagination);
      // A different page size is a different set of pages, so the reader goes back to the first.
      setPagination(next.pageSize === pagination.pageSize ? next : { ...next, pageIndex: 0 });
    },
    onSortingChange: (updater: MRT_Updater<SortingState>) => {
      const next = applyUpdater(updater, sorting);
      setSorting(next.length > 0 ? next : DEFAULT_SORTING);
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    state: {
      columnFilters,
      globalFilter,
      isLoading: bookmarksQuery.isLoading || isDeleting,
      pagination,
      showAlertBanner: bookmarksError,
      showProgressBars: bookmarksQuery.isFetching || isDeleting,
      showSkeletons: bookmarksQuery.isLoading,
      sorting,
    },
  };

  return <MRTNoMemo<BookmarkedTeamListItem> options={tableOptions} persistColumnVisibilityAs="marketplace-bookmarks-teams" cards={{ defaultSorting: DEFAULT_SORTING }} />;
}
